import { useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CopyPlus, Plus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const iso = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
export const mondayOf = (d = new Date()) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return iso(x); };
const addDays = (s, n) => { const [y, m, d] = s.split("-").map(Number); const x = new Date(y, m - 1, d + n); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const fmt = (s) => { const [, m, d] = s.split("-"); return `${d}/${m}`; };

// Un plat est programmé ce jour si la date est dans [start,end] et le jour de semaine autorisé.
function onDay(sp, day, wd) {
  if (sp.start_date && day < sp.start_date) return false;
  if (sp.end_date && day > sp.end_date) return false;
  if (!sp.start_date && !sp.end_date && !(sp.weekdays || []).length) return false; // permanents non datés : hors calendrier
  if ((sp.weekdays || []).length && !sp.weekdays.includes(wd)) return false;
  return true;
}

export function WeekCalendar({ slug, specials, onCreateAt, onEdit, onChanged }) {
  const [monday, setMonday] = useState(mondayOf());
  const [confirm, setConfirm] = useState(null); // { toMonday, conflicts }
  const [busy, setBusy] = useState(false);
  const todayIso = iso(new Date());
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

  const askDuplicate = async () => {
    const toMonday = addDays(monday, 7);
    try {
      const conflicts = await api.weekConflicts(slug, toMonday);
      setConfirm({ toMonday, conflicts });
    } catch { toast.error("Erreur"); }
  };
  const doDuplicate = async (replace) => {
    setBusy(true);
    try {
      const r = await api.duplicateSpecialsWeek(slug, { from_monday: monday, to_monday: confirm.toMonday, replace });
      toast.success(`${r.created} plat(s) dupliqué(s) vers la semaine du ${fmt(confirm.toMonday)}${r.skipped ? ` · ${r.skipped} doublon(s) ignoré(s)` : ""}${r.removed ? ` · ${r.removed} remplacé(s)` : ""}`);
      setConfirm(null); setMonday(confirm.toMonday); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de duplication"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="week-calendar" className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-3 md:p-4 mb-6">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button data-testid="week-prev" onClick={() => setMonday(addDays(monday, -7))} className="w-9 h-9 rounded-full grid place-items-center bg-[#0C1713] border border-[#1B3028] text-stone-300 hover:text-[#C69C4A]"><ChevronLeft className="w-4 h-4" /></button>
        <p data-testid="week-label" className="text-sm font-bold text-stone-100">Semaine du {fmt(monday)} au {fmt(addDays(monday, 6))}</p>
        <button data-testid="week-next" onClick={() => setMonday(addDays(monday, 7))} className="w-9 h-9 rounded-full grid place-items-center bg-[#0C1713] border border-[#1B3028] text-stone-300 hover:text-[#C69C4A]"><ChevronRight className="w-4 h-4" /></button>
        <button data-testid="week-today" onClick={() => setMonday(mondayOf())} className="text-xs text-stone-400 hover:text-[#C69C4A] underline-offset-2 hover:underline">Cette semaine</button>
        <Button data-testid="week-duplicate" onClick={askDuplicate} variant="outline" className="ml-auto rounded-full border-[#C69C4A]/60 text-[#C69C4A] hover:bg-[#C69C4A]/10 text-xs h-9">
          <CopyPlus className="w-4 h-4 mr-1.5" /> Dupliquer vers la semaine suivante
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
        {days.map((day, wd) => {
          const items = specials.filter((s) => onDay(s, day, wd));
          const isToday = day === todayIso;
          return (
            <div key={day} data-testid={`week-day-${day}`} className={`rounded-xl border p-2 min-h-[120px] flex flex-col ${isToday ? "border-[#C69C4A]/70 bg-[#14271F]" : "border-[#1B3028] bg-[#0C1713]"}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className={`text-[11px] font-bold uppercase tracking-wider ${isToday ? "text-[#C69C4A]" : "text-stone-400"}`}>{DAYS[wd].slice(0, 3)} <span className="text-stone-500 font-medium">{fmt(day)}</span></p>
                <button data-testid={`week-add-${day}`} onClick={() => onCreateAt(day)} className="w-6 h-6 rounded-full grid place-items-center text-stone-400 hover:text-[#0C1713] hover:bg-[#C69C4A]" title="Ajouter un plat du jour ce jour"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-1 flex-1">
                {items.length === 0 ? (
                  <button data-testid={`week-empty-${day}`} onClick={() => onCreateAt(day)} className="w-full h-full min-h-[60px] rounded-lg border border-dashed border-[#1B3028] text-[11px] text-stone-600 hover:text-[#C69C4A] hover:border-[#C69C4A]/50">+ Plat du jour</button>
                ) : items.map((s) => (
                  <button key={s.id} data-testid={`week-item-${s.id}-${day}`} onClick={() => onEdit(s)} className={`w-full text-left rounded-lg px-2 py-1.5 text-xs border ${s.active ? "bg-[#1A3D32]/60 border-[#2C5446] text-stone-100" : "bg-stone-800/50 border-stone-700 text-stone-400 line-through"}`}>
                    <span className="font-semibold block truncate">{s.name}</span>
                    <span className="text-[#C69C4A]">{eur(s.price)}</span>{s.quantity != null && <span className="text-stone-400"> · {s.quantity} p.</span>}
                    {(s.start_time || s.end_time) && <span className="text-stone-500 block">{s.start_time || "00:00"}–{s.end_time || "23:59"}</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="week-duplicate-dialog" className="rounded-3xl bg-[#FBF9F5] text-[#123026] border-none max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Dupliquer la semaine</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C615E]">
              Les plats du jour de la semaine du {fmt(monday)} seront copiés vers la semaine du {confirm ? fmt(confirm.toMonday) : ""} (dates décalées de 7 jours).
              Les quantités sont reprises comme objectifs de préparation ; aucun stock physique n'est modifié.
              {confirm?.conflicts?.length > 0 && (
                <span className="block mt-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-xs p-2.5">
                  <b>{confirm.conflicts.length} plat(s) déjà programmé(s)</b> cette semaine-là : {confirm.conflicts.map((c) => c.name).join(", ")}.<br />
                  « Conserver » les garde et ignore les doublons ; « Remplacer » les supprime avant la copie.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="week-duplicate-cancel" className="rounded-full">Annuler</AlertDialogCancel>
            {confirm?.conflicts?.length > 0 && (
              <Button data-testid="week-duplicate-replace" disabled={busy} onClick={() => doDuplicate(true)} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50">Remplacer</Button>
            )}
            <AlertDialogAction data-testid="week-duplicate-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); doDuplicate(false); }} className="rounded-full bg-[#123026] hover:bg-[#0A1C16] text-white">
              {confirm?.conflicts?.length > 0 ? "Conserver et compléter" : "Dupliquer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function StockThreshold({ slug }) {
  const [th, setTh] = useState(null);
  const [val, setVal] = useState("");
  useEffect(() => { api.getStockSettings(slug).then((r) => { setTh(r.low_stock_threshold); setVal(String(r.low_stock_threshold)); }).catch(() => {}); }, [slug]);
  const save = async () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Seuil invalide"); return; }
    try { const r = await api.setStockSettings(slug, { low_stock_threshold: n }); setTh(r.low_stock_threshold); toast.success(`Alerte stock faible à ${r.low_stock_threshold} portion(s)`); } catch { toast.error("Erreur"); }
  };
  if (th === null) return null;
  return (
    <div data-testid="stock-threshold" className="flex flex-wrap items-center gap-2 rounded-2xl bg-[#111F1A] border border-[#1B3028] px-3 py-2.5 mb-4 text-sm">
      <span className="text-stone-300">Alerter POS et Manager lorsqu'il reste</span>
      <input data-testid="stock-threshold-input" type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} className="w-16 h-8 rounded-lg bg-[#0C1713] border border-[#1B3028] text-center text-stone-100" />
      <span className="text-stone-300">portion(s) d'un plat du jour</span>
      {String(th) !== val && <Button data-testid="stock-threshold-save" onClick={save} size="sm" className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] h-8">Enregistrer</Button>}
      <span className="text-[11px] text-stone-500 ml-auto">Une seule alerte par passage sous le seuil, puis une à l'épuisement.</span>
    </div>
  );
}
